export type ScalarType = string | number | boolean;

function $buildOptions(options: any, names: string[]): string {
    if (options) {
        let anchor = '';
        const q = [] as string[];
        for (const key of Object.keys(options)) {
            if (names.indexOf(key) !== -1) {
                // the key is already consumed
                continue;
            }

            const value = options[key];

            if (key === 'anchor') {
                anchor = `#${$encode(value)}`;
            } else {
                $buildQuery(q, key, value);
            }
        }
        return (q.length > 0 ? '?' + q.join('&') : '') + anchor;
    } else {
        return '';
    }
}

function $buildQuery(q: string[], key: string, value: any) {
    if ($isScalarType(value)) {
        q.push(`${$encode(key)}=${$encode(value)}`);
    } else if (Array.isArray(value)) {
        for (const v of value) {
            $buildQuery(q, `${key}[]`, v);
        }
    } else if (value != null) {
        // i.e. non-null, non-scalar, non-array type
        for (const k of Object.keys(value)) {
            $buildQuery(q, `${key}[${k}]`, value[k]);
        }
    }
}

function $encode(value: any): string {
    return encodeURIComponent(value);
}

function $isScalarType(value: any): value is ScalarType {
    return (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
    );
}

function $isPresent(value: any): boolean {
    return value != null && ('' + value).length > 0;
}

function $hasPresentOwnProperty(options: any, key: string): boolean {
    return options && options.hasOwnProperty(key) && $isPresent(options[key]);
}

/** /api/v2/workspaces/pipeline(.:format) */
export function v2WorkspacesPipelineCustomizePath(options?: object): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'workspaces' +
        '/' +
        'customize_pipeline' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['format'])
    );
}

/** /api/v2/search/pipeline(.:format) */
export function v2WorkspacesPipelinePath(options?: object): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'search' +
        '/' +
        'pipeline' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['format'])
    );
}

/** /api/gon(.:format) */
export function gonPath(options?: object): string {
    return (
        '/' +
        'api' +
        '/' +
        'gon' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['format'])
    );
}

/** /api/v2/flow/:flow_id/users/:id/payment_methods(.:format) */
export function v2CreatePaymentMethodPath(
    flow_id: ScalarType,
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'flow' +
        '/' +
        flow_id +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        'payment_methods' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

/** /api/v2/users/validations/credit_card/date/:payment_method_id(.:format) */
export function v2ValidateUserCreditCardExpirationDatePath(
    flow_id: ScalarType,
    payment_method_id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'flow' +
        '/' +
        flow_id +
        '/' +
        'users' +
        '/' +
        'validations' +
        '/' +
        'credit_card' +
        '/' +
        'date' +
        '/' +
        payment_method_id +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['payment_method_id', 'format'])
    );
}

/** /api/v2/users/:id/plaid/token(.:format) */
export function v2SetupUserPlaidTokenPath(
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        'plaid' +
        '/' +
        'token' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

/** /api/v2/users/:id/chargeable_bank_account(.:format) */
export function v2CreateChargeableBankAccountPath(
    flow_id: ScalarType,
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'flow' +
        '/' +
        flow_id +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        'chargeable_bank_account' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

/** /api/v2/users/:id(.:format) */
export function v2GetCurrentUserPath(id: ScalarType, options?: object): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'users' +
        '/' +
        id +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

/** /api/v2/workspaces/:id/message(.:format) */
export function v2WorkspaceSendMessagePath(
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'workspaces' +
        '/' +
        id +
        '/' +
        'message' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

/** /api/v2/company/:company_id/workflows/:workflow_id(.:format) */
export function v2DeleteWorkflowPath(
    company_id: ScalarType,
    workflow_id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'company' +
        '/' +
        company_id +
        '/' +
        'workflows' +
        '/' +
        workflow_id +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['company_id', 'workflow_id', 'format'])
    );
}

/** /api/v2/company/:company_id/workflows/:workflow_id/duplicate(.:format) */
export function v2DuplicateWorkflowPath(
    company_id: ScalarType,
    workflow_id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'company' +
        '/' +
        company_id +
        '/' +
        'workflows' +
        '/' +
        workflow_id +
        '/' +
        'duplicate' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['company_id', 'workflow_id', 'format'])
    );
}

/** /api/v2/company/:company_id/workflows(.:format) */
export function v2GetWorkflowsForCompanyPath(
    company_id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'company' +
        '/' +
        company_id +
        '/' +
        'workflows' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['company_id', 'format'])
    );
}

/** /api/v2/users/:id/batch_emails(.:format) */
export function v2GetBatchEmailsPath(id: ScalarType, options?: object): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        'batch_emails' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

/** /api/v2/client_pending_tasks(.:format) */
export function v2CheckClientPendingTasksPath(options?: object): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'client_pending_tasks' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['format'])
    );
}

/** /api/v2/client_pending_task(.:format) */
export function v2CreateClientPendingTaskPath(options?: object): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'client_pending_task' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['format'])
    );
}

/** /api/v2/companies/:id/email/:email_id(.:format) */
export function v2CompanyGetEmailPath(
    id: ScalarType,
    email_id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'companies' +
        '/' +
        id +
        '/' +
        'email' +
        '/' +
        email_id +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'email_id', 'format'])
    );
}

/** /api/v2/companies/:id/package/:package_id(.:format) */
export function v2CompanyGetPackagePath(
    id: ScalarType,
    package_id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'companies' +
        '/' +
        id +
        '/' +
        'package' +
        '/' +
        package_id +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'package_id', 'format'])
    );
}

/** /api/v2/users/:id/notification_settings(.:format) */
export function v2GetNotificationSettingsPath(
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        'notification_settings' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

/** /api/v2/users/:id/mark_all_web_notifications_as_seen(.:format) */
export function v2MarkAllWebNotificationsAsSeenPath(
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        'mark_all_web_notifications_as_seen' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

/** /api/v2/users/:id/notification_settings(.:format) */
export function v2UpdateNotificationSettingsPath(
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        'notification_settings' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

/** /api/v2/users/:id/web_notifications(.:format) */
export function v2UserGetRecentWebNotificationsPath(
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        'web_notifications' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

/** /api/v2/events/:id/details(.:format) */
export function v2EventDetailsPath(id: ScalarType, options?: object): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'events' +
        '/' +
        id +
        '/' +
        'details' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

/** /api/v2/events(.:format) */
export function v2EventsSummaryPath(options?: object): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'events' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['format'])
    );
}

/** /api/v2/home/recent_activity(.:format) */
export function v2GetHomeRecentActivityPath(options?: object): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'home' +
        '/' +
        'recent_activity' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['format'])
    );
}

/** /api/v2/companies/:company_id/quickbooks(.:format) */
export function v2DestroyQuickbooksPath(
    company_id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'companies' +
        '/' +
        company_id +
        '/' +
        'quickbooks' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['company_id', 'format'])
    );
}

/** /api/v2/users/:id/session_configs/:session_config_id(.:format) */
export function v2UpdateSessionConfigPath(
    id: ScalarType,
    session_config_id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        'session_configs' +
        '/' +
        session_config_id +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'session_config_id', 'format'])
    );
}

/** /api/v2/users/:id/email_providers/:provider_type(.:format) */
export function v2DeleteEmailProviderPath(
    id: ScalarType,
    provider_type: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        'email_providers' +
        '/' +
        provider_type +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'provider_type', 'format'])
    );
}

/** /api/v2/workspaces/:id/members(.:format) */
export function v2GetWorkspaceMembersPath(
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'workspaces' +
        '/' +
        id +
        '/' +
        'members' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

/** /api/v2/users/:id/email_providers(.:format) */
export function v2GetEmailProvidersPath(
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        'email_providers' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

export function v2OnboardingSequencePath(
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        'onboarding_sequence' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

export function v2UserGetZapierApiKeyPath(
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        'integrations' +
        '/' +
        'zapier' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

export function v2UserTagsPath(id: ScalarType, options?: object): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        'user_tags' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

export function validateOnboardingPath(options?: object): string {
    return (
        '/' +
        'api' +
        '/' +
        'validate_onboarding' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['format'])
    );
}

export function v2UserConnectSocialAccountPath(
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        'connect_social_account' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

export function v2UserDisconnectSocialAccountPath(
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        'disconnect_social_account' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

export function v2CreateCalendarItemPath(
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        'calendar_item' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

export function v2UpdateCalendarItemPath(
    id: ScalarType,
    calendar_item_id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        'calendar_items' +
        '/' +
        calendar_item_id +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'calendar_item_id', 'format'])
    );
}

export function v2UsersChangePasswordPath(
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        'password' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

export function v2UserUpdateUserEmailPath(
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        'update_user_email' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

export function v2UsersSendPasswordLinkPath(
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        'send_password_link' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

export function v2GetCalendarEventsOnDatePath(
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        'calendar_events' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

export function v2UserUpdatePhoneNumberForTwoFactorAuthenticationPath(
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'users' +
        '/' +
        id +
        '/' +
        '2fa_phone_number' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

export function v2ActivateWorkspaceFilePath(
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'workspace_files' +
        '/' +
        id +
        '/' +
        'unblock' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

export function v2DeactivateWorkspaceFilePath(
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'workspace_files' +
        '/' +
        id +
        '/' +
        'block' +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

export function v2DeleteWorkspaceFilePath(
    id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'workspace_files' +
        '/' +
        id +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

export function v2UpdateWorkspaceStagesPath(
    id: ScalarType,
    stage_id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'workspaces' +
        '/' +
        id +
        '/' +
        'stages' +
        '/' +
        stage_id +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'stage_id', 'format'])
    );
}

export function v2WorkspacePath(id: ScalarType, options?: object): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'workspaces' +
        '/' +
        id +
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['id', 'format'])
    );
}

/** /api/v2/user/:user_id/companies(.:format) - Renamed for clarity */
export function v2UserConvertPreTrailerToTrailerPath(
    user_id: ScalarType,
    options?: object
): string {
    return (
        '/' +
        'api' +
        '/' +
        'v2' +
        '/' +
        'user' +
        '/' +
        user_id +
        '/' +
        'companies' + // Endpoint remains the same, function name clarifies intent
        ($hasPresentOwnProperty(options, 'format')
            ? '.' + (options as any).format
            : '') +
        $buildOptions(options, ['user_id', 'format'])
    );
}
